export interface TabDefinition<T extends string> {
  id: T;
  label: string;
  /** A count or flag shown beside the label, when there is something to say. */
  badge?: string;
  badgeKind?: "blocking" | "warning" | "quiet";
}

interface Props<T extends string> {
  tabs: readonly TabDefinition<T>[];
  active: T;
  onSelect: (id: T) => void;
}

/** The standalone tool's tab strip: arrow keys move between tabs. */
export default function Tabs<T extends string>({ tabs, active, onSelect }: Props<T>) {
  function step(by: number) {
    const index = tabs.findIndex((tab) => tab.id === active);
    const next = tabs[(index + by + tabs.length) % tabs.length];
    onSelect(next.id);
  }

  return (
    <div
      className="tabs"
      role="tablist"
      onKeyDown={(event) => {
        if (event.key === "ArrowRight") {
          event.preventDefault();
          step(1);
        }
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          step(-1);
        }
      }}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          id={`tab-${tab.id}`}
          aria-selected={tab.id === active}
          aria-controls={`panel-${tab.id}`}
          tabIndex={tab.id === active ? 0 : -1}
          className={tab.id === active ? "tab tab-on" : "tab"}
          onClick={() => onSelect(tab.id)}
        >
          {tab.label}
          {tab.badge === undefined ? null : (
            <span className={`tab-badge tab-badge-${tab.badgeKind ?? "quiet"}`}>{tab.badge}</span>
          )}
        </button>
      ))}
    </div>
  );
}
